import React, { useState } from 'react';
import Typewriter from 'typewriter-effect';
import { useIsChatLocked } from '../../../../../libs/hooks';
import { GetResponseOptions, useChat } from '../../../../../libs/hooks/useChat';
import { useAppDispatch, useAppSelector } from '../../../../../redux/app/hooks';
import { RootState } from '../../../../../redux/app/store';
import { updateAppErrorState } from '../../../../../redux/features/app/appSlice';
import { setIsLoadingMessages } from '../../../../../redux/features/conversations/conversationsSlice';
import { getParamValue } from '../../../../utils/UrlUtils';
import { Notifications } from '../../Notifications/Notifications';
import { Chat } from '../Chat';
import { ChatInput } from '../ChatInput';
import { ChatRoomPrompts } from '../ChatRoomPrompts';
import { IntelligentFilteringMessage } from '../IntelligentFilteringMessage';
// import { ExclamationCircleIcon } from '@heroicons/react/20/solid';

interface IChatTabProps {
    chatMode: string;
}

export const ChatTab = ({ chatMode }: IChatTabProps) => {
    const dispatch = useAppDispatch();
    const chat = useChat();

    const { notifications, isChatNotificationsEnabled } = useAppSelector((state: RootState) => state.app);
    const selectedId = useAppSelector((state: RootState) => state.conversations.selectedId);
    const { conversations, isLoadingMessages } = useAppSelector((state: RootState) => state.conversations);
    const messages = conversations[selectedId]?.messages;
    const feedback = conversations[selectedId]?.userFeedback;
    const chatTitle = conversations[selectedId]?.title;
    const botResponseStatus = conversations[selectedId]?.botResponseStatus;
    const intelligentFilteringBucketId = conversations[selectedId]?.intelligentFilteringBucketId;
    const recentAutoRename = conversations[selectedId]?.recentAutoRename;
    const [messageIdParam, setMessageIdParam] = useState<string>('');
    const { isLockedChat } = useIsChatLocked(conversations, selectedId);
    const scrollViewTargetRef = React.useRef<HTMLDivElement>(null);
    const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);

    const [isDraggingOver, setIsDraggingOver] = React.useState(false);

    const { serviceInfo } = useAppSelector((state: RootState) => state.app);
    const threadId = conversations[selectedId]?.threadId ?? '';
    const chatDisabled = !serviceInfo.isInsightFeatureEnabled && threadId;

    const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };
    const onDragLeave = (e: React.DragEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    React.useEffect(() => {
        const messageId = getParamValue('messageid');
        if (messageId) {
            const messageExist = messages?.find((m) => m.id === messageId);
            if (messageExist) {
                setShouldAutoScroll(false);
                setMessageIdParam(messageId);
            }
        }
    }, [messages]);

    React.useEffect(() => {
        if (!conversations[selectedId].isSaved) return;
        const getMessages = async () => {
            dispatch(setIsLoadingMessages(true));
            await chat.getChatMessages(selectedId);
            await chat.getChatParticipants(selectedId);
            dispatch(setIsLoadingMessages(false));
        };

        if (!messages || !messages.length) {
            getMessages().catch(() => {
                dispatch(setIsLoadingMessages(false));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    React.useEffect(() => {
        if (!shouldAutoScroll) return;
        scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
    }, [messages, feedback, shouldAutoScroll, isLoadingMessages]);

    React.useEffect(() => {
        const onScroll = () => {
            if (!scrollViewTargetRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollViewTargetRef.current;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            setShouldAutoScroll(isAtBottom);
        };

        if (!scrollViewTargetRef.current) return;

        const currentScrollViewTarget = scrollViewTargetRef.current;

        currentScrollViewTarget.addEventListener('scroll', onScroll);
        return () => {
            currentScrollViewTarget.removeEventListener('scroll', onScroll);
        };
    }, []);

    const handleSubmit = async (options: GetResponseOptions) => {
        setShouldAutoScroll(true);

        const askResult = await chat.getResponse(options);

        if (null === askResult) {
            dispatch(updateAppErrorState(true));
        }

        return askResult;
    };

    return (
        <>
            <div
                ref={messageIdParam ? undefined : scrollViewTargetRef}
                className={`${isDraggingOver ? 'opacity-75 blur-sm' : ''} flex-1 overflow-y-auto bg-white leading-6 shadow-md dark:bg-slate-900`}>
                <div className="items-center text-center">
                    <h1 className="transition-all duration-500 text-4xl pt-3">
                        {!messages || messages.length < 2 ? (
                            <>
                                {import.meta.env.VITE_APP_NAME_PREFIX && <>{import.meta.env.VITE_APP_NAME_PREFIX} </>}
                                <span className="bg-gradient-to-br from-primary-600 via-primary-300 to-primary-200 inline-block text-transparent bg-clip-text">
                                    {import.meta.env.VITE_APP_NAME}
                                </span>
                            </>
                        ) : (
                            <>
                                {!recentAutoRename ? (
                                    chatTitle
                                ) : (
                                    <>
                                        <Typewriter
                                            options={{ delay: 20 }}
                                            onInit={(typewriter) => {
                                                typewriter.typeString(chatTitle).start();
                                            }}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </h1>
                </div>
                {(!messages || messages.length < 2) && !isLockedChat && !chatDisabled && (
                    <ChatRoomPrompts onSubmit={handleSubmit} />
                )}
                <Chat
                    messageId={messageIdParam}
                    chatMode={chatMode}
                    messages={messages}
                    loadingMessages={isLoadingMessages}
                />
            </div>

            {isChatNotificationsEnabled && notifications && notifications.length > 0 && (
                <Notifications messages={notifications} />
            )}

            {!isLockedChat &&
                !conversations[selectedId]?.hidden &&
                (chatDisabled ? (
                    <div className="p-10">
                        The Insight feature has been turned off, and you can no longer access this chat. Please start a
                        new chat.
                    </div>
                ) : (
                    <>
                        {intelligentFilteringBucketId &&
                            intelligentFilteringBucketId !== '' &&
                            (!botResponseStatus ||
                                botResponseStatus.length === 0 ||
                                botResponseStatus.indexOf('Uploading') > -1) && <IntelligentFilteringMessage />}
                        <div onDragEnter={onDragEnter} onDragOver={onDragEnter} onDragLeave={onDragLeave}>
                            <ChatInput
                                isDraggingOver={isDraggingOver}
                                onDragLeave={onDragLeave}
                                onSubmit={handleSubmit}
                            />
                        </div>
                    </>
                ))}
        </>
    );
};
